import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../../../../../redux/ducks/Loading";
import { initializeForm, setDirtyWC, setValueWC } from "../../../../../redux/ducks/EditableTable";
import { setRefreshWorkCosts } from "../../../../../redux/ducks/ProjectManagement";
import { ExclamationCircleOutlined, EyeOutlined, SaveOutlined, StopOutlined, WarningOutlined } from "@ant-design/icons";
import { Table, Input, Row, Col, Modal, Tooltip, notification, Popover } from "antd";
import Button from "../../../../../components/functional_components/Button/PFButton";
import RdaDetailModal from "./RdaDetailModal";
import ProjectCostPlan from "../../../../../services/api/ProjectCostPlan";
import { cloneDeep, get, indexOf, isEmpty, lowerCase, toLower, toNumber } from "lodash";
import { mapMonths } from "../../../../../utils/properties";
import {
  elaborateWCdata,
  formatNumber,
  getClassName,
  showError,
  showSuccess,
  unformatNumber,
} from "../../../../../utils/utilities";

const WCTable = (props) => {
  const { confirm } = Modal;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const userInfo = useSelector((state) => get(state.userInfoDuck, "userInfo", {}));
  const getCustomForm = useSelector((state) => get(state.EditableTableDuck, "customForm", []));
  const projectManagement = useSelector((state) => get(state.ProjectManagementDuck, "project", {}));
  const openPlanning = useSelector((state) => get(state.ProjectManagementDuck, "openPlanning", {}));
  const refreshWC = useSelector((state) => state.ProjectManagementDuck.refreshWorkCost);
  const closed = useSelector((state) => get(state.ProjectManagementDuck, "closed", {}));
  const rdaJobRunning = useSelector((state) => get(state.ProjectManagementDuck, "rdaJobRunning", false));
  const runningCostJobRunning = useSelector((state) =>
    get(state.ProjectManagementDuck, "runningCostJobRunning", false)
  );
  const [state, setState] = useState({
    wcContentExpanded: [],
    columnsDataTable: [],
    columnsExpanded: [],
    wbsData: {},
    arrDirtyForm: [],
    expandedRowKeys: [],
    canEditToZeroTasks: [],
    runningMonthNumber: 0,
    runningYearNumber: 0,
    runningMonth: "",
  });
  const [rdaDetailModalState, setRdaDetailModalState] = useState({ open: false, projectTaskId: null });

  // task statuses where it is possible to edit future values only if the new value is zero
  const canEditToZeroTaskStatus = ["BLOCKED", "CLOSED", "COMPLETED"];

  // Manage scrolls
  const containerRef = useRef(null);
  const [scrollPosition, setScrollPosition] = useState({ x: 0, y: 0 });
  const [syncEnabled, setSyncEnabled] = useState(true);
  const expandedTablesRefs = useRef(new Map());
  const isScrolling = useRef(false);
  const scrollingElementRef = useRef(null);
  const rafIdRef = useRef(null);
  const lastScrollLeft = useRef(0);
  const scrollListeners = useRef([]);
  const totalTableRef = props.totalTableRef;

  useEffect(() => {
    // I need inizialize columns on use effect for render it when I change language and when I expand columns to use "handleChange"
    if (!isEmpty(props.dataContentWP)) {
      columns(props.allData);
    }
    if (!isEmpty(state.wbsData)) {
      columnsExpanded(props.allData);
    }
  }, [
    props.dataContentWP,
    props.allData,
    currentLanguage,
    getCustomForm,
    refreshWC,
    state.wcContentExpanded,
    state.runningMonthNumber,
  ]);

  useEffect(() => {
    if (!props.allData || !props.allData.projectCostPlanValues) return;

    const canEditToZeroTasks = props.allData.projectCostPlanValues
      .filter((item) => canEditToZeroTaskStatus.includes(item.projectTaskStatus))
      .map((item) => item.projectTaskId);

    // The running month is the current month: it is always the month after open month
    const openMonthNumber = mapMonths[lowerCase(props.allData.openMonth)];
    const runningMonthNumber = openMonthNumber + 1 > 12 ? 1 : openMonthNumber + 1;
    const runningYearNumber =
      openMonthNumber + 1 > 12 ? toNumber(props.allData.openYear) + 1 : toNumber(props.allData.openYear);
    const runningMonth = Object.keys(mapMonths)
      .find((key) => mapMonths[key] === runningMonthNumber)
      .toUpperCase();

    setState((prevState) => ({
      ...prevState,
      canEditToZeroTasks,
      runningMonthNumber,
      runningYearNumber,
      runningMonth,
    }));

    // add project tasks with rdaAlert == true to rdaAlertProjectTaskIds
    const initialRdaAlertProjectTaskIds = [];
    props.allData.projectCostPlanValues.forEach((item) => {
      if (item.rda && item.rda.rdaAlert && !initialRdaAlertProjectTaskIds.includes(item.projectTaskId)) {
        initialRdaAlertProjectTaskIds.push(item.projectTaskId);
      }
    });
    props.setRdaAlertProjectTaskIds(initialRdaAlertProjectTaskIds);

    // add project tasks with plannedLessThanRunning == true to plannedRunningErrorProjectTaskIds
    const initialPlannedRunningErrorProjectTaskIds = props.allData.projectCostPlanValues
      .filter((item) => item.plannedLessThanRunning) // true if at least one planned value for the running month < than the corresponding running value
      .map((item) => item.projectTaskId);
    props.setPlannedRunningErrorProjectTaskIds(initialPlannedRunningErrorProjectTaskIds);
  }, [props.allData]);

  const handleTableScroll = useCallback(
    (e) => {
      if (!syncEnabled || isScrolling.current) return;

      const scrollLeft = e.target.scrollLeft;

      // Solo se lo scroll è cambiato significativamente
      if (Math.abs(lastScrollLeft.current - scrollLeft) < 2) return;

      // Aggiorna immediatamente per UX
      setScrollPosition((prev) => ({ ...prev, x: scrollLeft }));

      // Cancella eventuali rAF precedenti
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }

      scrollingElementRef.current = e.target;
      isScrolling.current = true;
      lastScrollLeft.current = scrollLeft;

      // Usa requestAnimationFrame per sincronizzare con il frame di render
      rafIdRef.current = requestAnimationFrame(() => {
        syncAllTables(scrollLeft);
      });
    },
    [syncEnabled]
  );

  // Funzione ottimizzata per sincronizzare tutte le tabelle
  const syncAllTables = useCallback(
    (scrollLeft) => {
      if (!syncEnabled) {
        isScrolling.current = false;
        return;
      }

      try {
        // Usa un Set per evitare duplicati
        const scrollElements = new Set();

        // Aggiungi elementi della tabella principale
        if (containerRef.current) {
          const mainScrollElements = containerRef.current.querySelectorAll(".ant-table-body, .ant-table-content");
          mainScrollElements.forEach((el) => {
            if (el && el !== scrollingElementRef.current) {
              scrollElements.add(el);
            }
          });
        }

        // Aggiungi elementi delle tabelle espanse
        expandedTablesRefs.current.forEach((container) => {
          if (container) {
            const expandedScrollElements = container.querySelectorAll(".ant-table-body, .ant-table-content");
            expandedScrollElements.forEach((el) => {
              if (el && el !== scrollingElementRef.current) {
                scrollElements.add(el);
              }
            });
          }
        });

        // Aggiungi elementi della tabella dei totali
        if (totalTableRef.current) {
          const mainScrollElements = totalTableRef.current.querySelectorAll(".ant-table-body, .ant-table-content");
          mainScrollElements.forEach((el) => {
            if (el && el !== scrollingElementRef.current) {
              scrollElements.add(el);
            }
          });
        }

        // Applica lo scroll a tutti gli elementi raccolti
        scrollElements.forEach((el) => {
          if (Math.abs(el.scrollLeft - scrollLeft) > 1) {
            el.scrollLeft = scrollLeft;
          }
        });
      } finally {
        // Resetta lo stato di scrolling dopo un breve ritardo
        requestAnimationFrame(() => {
          isScrolling.current = false;
          scrollingElementRef.current = null;
        });
      }
    },
    [syncEnabled]
  );

  // Gestione migliorata dei listener di scroll con un unico listener per elemento
  const setupScrollListeners = useCallback(() => {
    removeScrollListeners();

    if (!syncEnabled) return;

    const addListenersToContainer = (container, selector) => {
      if (!container) return;

      const elements = container.querySelectorAll(selector);
      elements.forEach((element) => {
        if (element) {
          // Rimuovi i listener esistenti per evitare duplicati
          element.removeEventListener("scroll", handleTableScroll);
          // Aggiungi nuovo listener con { passive: true } per migliori performance
          element.addEventListener("scroll", handleTableScroll, { passive: true });
          scrollListeners.current.push({
            element,
            listener: handleTableScroll,
          });
        }
      });
    };

    // Applica ai contenitori principali
    addListenersToContainer(containerRef.current, ".ant-table-body, .ant-table-content");
    addListenersToContainer(totalTableRef.current, ".ant-table-body, .ant-table-content");

    // Applica a tutti i contenitori espansi
    expandedTablesRefs.current.forEach((container) => {
      addListenersToContainer(container, ".ant-table-body, .ant-table-content");
    });
  }, [syncEnabled, handleTableScroll]);

  const removeScrollListeners = useCallback(() => {
    scrollListeners.current.forEach(({ element, listener }) => {
      element.removeEventListener("scroll", listener);
    });
    scrollListeners.current = [];
  }, []);

  const toggleSync = useCallback(() => {
    setSyncEnabled((prev) => !prev);
  }, []);

  // Effetto per gestire i listener quando cambia syncEnabled o le righe espanse
  useEffect(() => {
    // Cancella sempre i precedenti requestAnimationFrame
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }

    // Rimuovi i vecchi listener
    removeScrollListeners();

    // Setup listener con un piccolo ritardo per garantire che il DOM sia aggiornato
    const timeoutId = setTimeout(() => {
      setupScrollListeners();
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
      removeScrollListeners();
    };
  }, [syncEnabled, state.expandedRowKeys, props.totalTableExpandedChange, setupScrollListeners, removeScrollListeners]);

  // Reset dello stato di scrolling quando si espande o collassa una riga
  useEffect(() => {
    isScrolling.current = false;
    scrollingElementRef.current = null;
  }, [state.expandedRowKeys]);

  const columns = (allData) => {
    let objCol = [
      {
        title: t("projectsManagement.planning.workCostsTab.activityResource"),
        width: 200,
        fixed: "left",
        render: (_, item) => {
          return canEditToZeroTaskStatus.includes(item.projectTaskStatus) ? (
            <Row gutter={16} align="middle">
              <Col span={20}>{item.projectTaskName}</Col>
              <Col span={4} title={t("projectsManagement.planning.workCostsTab.activityResourceClosed")}>
                <StopOutlined style={{ color: "#dd042b" }} />
              </Col>
            </Row>
          ) : (
            item.projectTaskName
          );
        },
      },
    ];
    if (allData.projectCostPlanValues && !isEmpty(allData.projectCostPlanValues)) {
      allData.projectCostPlanValues[0].amountValues.forEach((item, i) => {
        let childrenArr = [];
        Object.keys(item.columnValueMap).forEach((cvm) => {
          if (cvm !== "key") {
            if (mapMonths[lowerCase(cvm)] !== undefined || cvm === "PRESENT") {
              const timeMonth = getClassName(cvm, allData.openMonth, allData.openYear, item.year);
              // Months
              if (cvm !== "PRESENT") {
                // Print column only if not "PRESENT" column
                if (timeMonth === "present") {
                  // If I'm in current month, I print the double column
                  if (allData.closed) {
                    childrenArr.push({
                      title: t(`projectsManagement.planning.workCostsTab.${toLower(cvm)}`),
                      width: 100,
                      className: "table-value-right ",
                      dataIndex: "consAmountOpenMonth",
                      render: (amount) =>
                        formatNumber({
                          value: amount ? amount : 0,
                          suffix: "EUR",
                          lang: currentLanguage,
                          decimalScale: 0,
                        }),
                    });
                  } else {
                    childrenArr.push({
                      title: `${t(`projectsManagement.planning.workCostsTab.${toLower(cvm)}`)} Plan`,
                      width: 100,
                      dataIndex: ["amountValues", i, "columnValueMap", cvm],
                      className: "table-value-right",
                      render: (amount) =>
                        formatNumber({
                          value: amount ? amount : 0,
                          suffix: "EUR",
                          lang: currentLanguage,
                          decimalScale: 0,
                        }),
                    });
                    childrenArr.push({
                      title: `${t(`projectsManagement.planning.workCostsTab.${toLower(cvm)}`)} Act`,
                      width: 100,
                      dataIndex: ["amountValues", i, "columnValueMap", "PRESENT"],
                      className: `table-value-right ${timeMonth}`,
                      render: (amount) =>
                        formatNumber({
                          value: amount ? amount : 0,
                          suffix: "EUR",
                          lang: currentLanguage,
                          decimalScale: 0,
                        }),
                    });
                  }
                } else {
                  childrenArr.push({
                    title: t(`projectsManagement.planning.workCostsTab.${toLower(cvm)}`),
                    width: 100,
                    dataIndex: ["amountValues", i, "columnValueMap", cvm],
                    className: `table-value-right ${timeMonth}`,
                    render: (amount, record) => {
                      let classname = "";
                      if (
                        timeMonth === "future" &&
                        isRunningMonth(cvm, item.year) &&
                        ((amount || 0) < record.runningAmount || record.plannedLessThanRunning === true)
                      ) {
                        classname = "PFred fw-bold";
                      }
                      return formatNumber({
                        value: amount ? amount : 0,
                        suffix: "EUR",
                        lang: currentLanguage,
                        decimalScale: 0,
                        className: classname,
                      });
                    },
                  });
                  if (timeMonth === "future" && isRunningMonth(cvm, item.year)) {
                    childrenArr.push({
                      title: `${t(`projectsManagement.planning.workCostsTab.${toLower(cvm)}`)} Run`,
                      width: 100,
                      dataIndex: ["runningAmount"],
                      className:
                        `table-value-right running` + (runningCostJobRunning ? " alert-currently-updating" : ""),
                      render: (amount) => {
                        return (
                          <Popover
                            trigger={["hover", "focus"]}
                            content={
                              runningCostJobRunning
                                ? t("projectsManagement.planning.workCostsTab.alertCurrentlyUpdating")
                                : ""
                            }
                            placement="right"
                            classNames={{
                              root: "tooltip-like-popover",
                            }}
                          >
                            <Row>
                              {formatNumber({
                                value: amount ? amount : 0,
                                suffix: "EUR",
                                lang: currentLanguage,
                                decimalScale: 0,
                              })}
                            </Row>
                          </Popover>
                        );
                      },
                    });
                  }
                }
              }
            } else {
              // AP, AS, Total, total-1 total budget
              childrenArr.push({
                title: t(`projectsManagement.planning.workCostsTab.${toLower(cvm)}`),
                width: 100,
                dataIndex: ["amountValues", i, "columnValueMap", cvm],
                fixed: "right",
                className: "table-value-right highlighted",
                render: (amount) =>
                  formatNumber({ value: amount ? amount : 0, suffix: "EUR", lang: currentLanguage, decimalScale: 0 }),
              });
            }
          }
        });
        objCol.push({
          title: item.year,
          width: 100,
          className: "table-value-right",
          children: childrenArr,
        });
      });

      // RDA
      objCol.push({
        title: t(`projectsManagement.planning.workCostsTab.rda`),
        width: 150,
        dataIndex: ["rda"],
        fixed: "right",
        className: "table-value-right highlighted" + (rdaJobRunning ? " alert-currently-updating" : ""),
        render: (rdaObject, record) => getRdaCellContent(rdaObject, record, false),
      });

      // Total, total-1 total budget
      objCol.push({
        title: t(`projectsManagement.planning.workCostsTab.${toLower("TOT")}`),
        width: 100,
        dataIndex: ["totalAmountMap", "TOT"],
        fixed: "right",
        className: "table-value-right highlighted",
        render: (amount, record) =>
          formatNumber({
            value: amount ? amount : 0,
            suffix: record.isHour ? "HOUR" : "EUR",
            lang: currentLanguage,
            decimalScale: 0,
          }),
      });

      objCol.push({
        title: t(`projectsManagement.planning.workCostsTab.${toLower("TOT_PREV_MONTH")}`),
        width: 100,
        dataIndex: ["totalAmountMap", "TOT_PREV_MONTH"],
        fixed: "right",
        className: "table-value-right highlighted",
        render: (amount, record) =>
          formatNumber({
            value: amount ? amount : 0,
            suffix: record.isHour ? "HOUR" : "EUR",
            lang: currentLanguage,
            decimalScale: 0,
          }),
      });

      objCol.push({
        title: t(`projectsManagement.planning.workCostsTab.${toLower("TOT_BUDGET")}`),
        width: 100,
        dataIndex: ["totalAmountMap", "TOT_BUDGET"],
        fixed: "right",
        className: "table-value-right highlighted",
        render: (amount, record) =>
          formatNumber({
            value: amount ? amount : 0,
            suffix: record.isHour ? "HOUR" : "EUR",
            lang: currentLanguage,
            decimalScale: 0,
          }),
      });
    }
    setState((prevState) => ({ ...prevState, columnsDataTable: objCol }));
  };

  const isRunningMonth = (monthToCheck, yearToCheck) => {
    return (
      state.runningMonthNumber === mapMonths[lowerCase(monthToCheck)] &&
      state.runningYearNumber === toNumber(yearToCheck)
    );
  };

  const getRdaCellContent = (rdaObject, record, isServiceRow) => {
    if (
      isServiceRow &&
      (record.activity.includes("SERVMOI") ||
        record.activity.includes("SERVMOE") ||
        record.activity === "work" ||
        record.activity === "costs")
    ) {
      // non esistono valori RDA/Committed per la manodopera o per la riga costi
      return "";
    }
    return (
      <Popover
        trigger={["hover", "focus"]}
        content={rdaJobRunning ? t("projectsManagement.planning.workCostsTab.alertCurrentlyUpdating") : ""}
        placement="right"
        classNames={{
          root: "tooltip-like-popover",
        }}
      >
        <Row gutter={4} align={"middle"} justify={"space-between"} style={{ paddingLeft: "2px" }}>
          <Button
            currentIcon={<EyeOutlined />}
            clickCallback={() =>
              setRdaDetailModalState({
                open: true,
                projectId: props.projectId,
                projectTaskId: record.projectTaskId,
                serviceId: isServiceRow ? record.activity : null,
              })
            }
          />
          <Col style={{ justifyItems: "center" }}>
            {rdaObject && rdaObject.rdaAlert && (
              <Tooltip title={t("projectsManagement.planning.rdaDetail.rdaAlert")} placement="top">
                <WarningOutlined style={{ color: "#ff0000", cursor: "pointer" }} />
              </Tooltip>
            )}
            {rdaObject && rdaObject.rdaExtraBudget && (
              <Tooltip title={t("projectsManagement.planning.rdaDetail.extraBudget")} placement="bottom">
                <div className="extraBudget">
                  <span>&euro;</span>
                </div>
              </Tooltip>
            )}
          </Col>
          <Col>
            {formatNumber({
              value: rdaObject && rdaObject.rda && rdaObject.rda > 0 ? rdaObject.rda : 0,
              suffix: "EUR",
              lang: currentLanguage,
              decimalScale: 0,
            })}
          </Col>
        </Row>
      </Popover>
    );
  };

  const columnsExpanded = (allData) => {
    let objCol = [
      {
        title: t("projectsManagement.planning.workCostsTab.activityResource"),
        dataIndex: "activity",
        width: 247,
        fixed: "left",
        render: (activity) => {
          if (activity === "work") return t("projectsManagement.planning.workCostsTab.work");
          else if (activity === "costs") return t("projectsManagement.planning.workCostsTab.costs");
          return activity;
        },
      },
    ];

    if (allData.projectCostPlanValues && !isEmpty(allData.projectCostPlanValues)) {
      allData.projectCostPlanValues[0].amountValues.forEach((item, i) => {
        let childrenArr = [];
        Object.keys(item.columnValueMap).forEach((cvm) => {
          if (indexOf(["key", "isOnlyVisual", "isHour", "activity"], cvm) === -1) {
            if (mapMonths[lowerCase(cvm)] !== undefined || cvm === "PRESENT") {
              const timeMonth = getClassName(cvm, allData.openMonth, allData.openYear, item.year);
              // Months
              if (cvm !== "PRESENT") {
                // Print column only if not "PRESENT" column
                if (timeMonth === "present") {
                  // If I'm in current month, I print the double column (plan and actual)
                  if (allData.closed) {
                    childrenArr.push({
                      title: t(`projectsManagement.planning.workCostsTab.${toLower(cvm)}`),
                      width: 100,
                      className: "table-value-right past",
                      dataIndex: ["PRESENT"],
                      render: (amount, record) =>
                        formatNumber({
                          value: amount ? amount : 0,
                          suffix: record.isHour ? "HOUR" : "EUR",
                          lang: currentLanguage,
                          decimalScale: 0,
                        }),
                    });
                  } else {
                    childrenArr.push({
                      title: `${t(`projectsManagement.planning.workCostsTab.${toLower(cvm)}`)} Plan`,
                      width: 100,
                      dataIndex: [i, "columnValueMap", cvm],
                      className: "table-value-right",
                      render: (amount, record) => {
                        return formatNumber({
                          value: amount ? amount : 0,
                          suffix: record.isHour ? "HOUR" : "EUR",
                          lang: currentLanguage,
                          decimalScale: 0,
                        });
                      },
                    });
                    childrenArr.push({
                      title: `${t(`projectsManagement.planning.workCostsTab.${toLower(cvm)}`)} Act`,
                      width: 100,
                      dataIndex: ["PRESENT"],
                      className: `table-value-right ${timeMonth}`,
                      render: (amount, record) =>
                        formatNumber({
                          value: amount ? amount : 0,
                          suffix: record.isHour ? "HOUR" : "EUR",
                          lang: currentLanguage,
                          decimalScale: 0,
                        }),
                    });
                  }
                } else {
                  childrenArr.push({
                    title: t(`projectsManagement.planning.workCostsTab.${toLower(cvm)}`),
                    width: 100,
                    dataIndex: [i, "columnValueMap", cvm],
                    className: `table-value-right ${timeMonth}`,
                    render: (amount, record) => {
                      let classname = "";
                      if (
                        timeMonth === "future" &&
                        isRunningMonth(cvm, item.year) &&
                        ((record.isHour && (amount || 0) < record.runningQuantity) ||
                          (!record.isHour && (amount || 0) < record.runningAmount))
                      ) {
                        classname = "PFred fw-bold";
                      }
                      return formatNumber({
                        name: cvm,
                        value: record.isOnlyVisual
                          ? amount
                          : getValueForm(
                              amount,
                              mapMonths[lowerCase(cvm)],
                              record.isHour ? "quantity" : "amount",
                              record.activity,
                              item.year,
                              record.projectTaskId
                            ),
                        lang: currentLanguage,
                        id: `${cvm}-${item.year}-${record.projectTaskId}-${record.activity}-${record.isHour ? "work" : "cost"}`,
                        displayType: showInput({
                          type: "displayType",
                          timeMonth,
                          isOnlyVisual: record.isOnlyVisual,
                          isEditable: record.isEditable,
                          canEditPlanning: userInfo.canEditPlanning,
                          allDataClosed: allData.closed,
                          projectEditable: props.projectEditable,
                          projectTaskId: record.projectTaskId,
                        }),
                        decimalScale: showInput({
                          type: "decimalScale",
                          timeMonth,
                          isOnlyVisual: record.isOnlyVisual,
                          isEditable: record.isEditable,
                          canEditPlanning: userInfo.canEditPlanning,
                          allDataClosed: allData.closed,
                          projectEditable: props.projectEditable,
                          projectTaskId: record.projectTaskId,
                        }),
                        suffix: record.isHour ? "HOUR" : "EUR",
                        customInput: Input,
                        className: classname,
                        // isAllowed: ({ floatValue }) => (record.wbsId !== "99-01" ? floatValue >= 0 : true), // comment this line to allow all values
                        onChange: (e) =>
                          handleChange(
                            e,
                            mapMonths[lowerCase(cvm)],
                            record.isHour ? "quantity" : "amount",
                            record.activity,
                            item.year,
                            record.projectTaskId
                          ),
                      });
                    },
                  });
                  if (timeMonth === "future" && isRunningMonth(cvm, item.year)) {
                    childrenArr.push({
                      title: `${t(`projectsManagement.planning.workCostsTab.${toLower(cvm)}`)} Run`,
                      width: 100,
                      className:
                        `table-value-right running` + (runningCostJobRunning ? " alert-currently-updating" : ""),
                      render: (amount, record) => {
                        return (
                          <Popover
                            trigger={["hover", "focus"]}
                            content={
                              runningCostJobRunning
                                ? t("projectsManagement.planning.workCostsTab.alertCurrentlyUpdating")
                                : ""
                            }
                            placement="right"
                            classNames={{
                              root: "tooltip-like-popover",
                            }}
                          >
                            <Row>
                              {formatNumber({
                                value: record.isHour ? record.runningQuantity : record.runningAmount,
                                suffix: record.isHour ? "HOUR" : "EUR",
                                lang: currentLanguage,
                                decimalScale: 0,
                              })}
                            </Row>
                          </Popover>
                        );
                      },
                    });
                  }
                }
              }
            }
          }
        });

        objCol.push({
          title: item.year,
          width: 100,
          className: "table-value-right",
          children: childrenArr,
        });
      });
      // RDA
      objCol.push({
        title: t(`projectsManagement.planning.workCostsTab.rda`),
        width: 150,
        dataIndex: ["rda"],
        fixed: "right",
        className: "table-value-right highlighted" + (rdaJobRunning ? " alert-currently-updating" : ""),
        render: (rdaObject, record) => getRdaCellContent(rdaObject, record, true),
      });
      // Total, total-1 total budget
      objCol.push({
        title: t(`projectsManagement.planning.workCostsTab.${toLower("TOT")}`),
        width: 100,
        dataIndex: ["totalMap", "TOT"],
        fixed: "right",
        className: "table-value-right highlighted",
        render: (amount, record) =>
          formatNumber({
            value: amount ? amount : 0,
            suffix: record.isHour ? "HOUR" : "EUR",
            lang: currentLanguage,
            decimalScale: 0,
          }),
      });

      objCol.push({
        title: t(`projectsManagement.planning.workCostsTab.${toLower("TOT_PREV_MONTH")}`),
        width: 100,
        dataIndex: ["totalMap", "TOT_PREV_MONTH"],
        fixed: "right",
        className: "table-value-right highlighted",
        render: (amount, record) =>
          formatNumber({
            value: amount ? amount : 0,
            suffix: record.isHour ? "HOUR" : "EUR",
            lang: currentLanguage,
            decimalScale: 0,
          }),
      });

      objCol.push({
        title: t(`projectsManagement.planning.workCostsTab.${toLower("TOT_BUDGET")}`),
        width: 100,
        dataIndex: ["totalMap", "TOT_BUDGET"],
        fixed: "right",
        className: "table-value-right highlighted",
        render: (amount, record) =>
          formatNumber({
            value: amount ? amount : 0,
            suffix: record.isHour ? "HOUR" : "EUR",
            lang: currentLanguage,
            decimalScale: 0,
          }),
      });
    }
    setState((prevState) => ({ ...prevState, columnsExpanded: objCol }));
  };

  const showInput = (showProps) => {
    let displayType = "text";
    let decimalScale = 0;
    if (
      showProps.timeMonth === "future" &&
      showProps.canEditPlanning &&
      !showProps.isOnlyVisual &&
      showProps.projectEditable &&
      projectManagement.enabled
    ) {
      if ((openPlanning && closed) || !showProps.allDataClosed) {
        if (showProps.isEditable) {
          displayType = "input";
          decimalScale = 2;
        }
      }
    }

    return showProps.type === "displayType" ? displayType : decimalScale;
  };

  const handleChange = (e, month, type, service, year, projectTaskId) => {
    const value = unformatNumber(e.target.value, currentLanguage, type);
    const oldValue = unformatNumber(e.target.defaultValue, currentLanguage, type);
    handleChangeValue(value, month, type, service, year, projectTaskId, oldValue);
  };
  const handleChangeValue = (value, month, type, service, year, projectTaskId, oldValue = null) => {
    let customForm = getCustomForm;
    if (isEmpty(customForm)) {
      customForm.push({
        projectTaskId: projectTaskId,
        year: year,
        month: month,
        serviceId: service,
        quantity: type === "quantity" ? value : 0,
        amount: type === "amount" ? value : 0,
      });
    } else {
      const foundIndex = customForm.findIndex(
        (item) =>
          item.projectTaskId === projectTaskId &&
          item.year === year &&
          item.month === month &&
          item.serviceId === service
      );
      if (foundIndex !== -1) {
        if (type === "quantity") {
          customForm[foundIndex].quantity = value;
          if (state.canEditToZeroTasks.includes(projectTaskId) && customForm[foundIndex].quantity === 0) {
            customForm[foundIndex].amount = 0;
          }
        } else {
          customForm[foundIndex].amount = value;
        }
      } else {
        customForm.push({
          projectTaskId: projectTaskId,
          year: year,
          month: month,
          serviceId: service,
          quantity: type === "quantity" ? value : 0,
          amount: type === "amount" ? value : 0,
        });
      }
    }
    let arrDirtyForm = state.arrDirtyForm;
    arrDirtyForm.push(projectTaskId);

    dispatch(setValueWC(customForm));
    dispatch(setDirtyWC(true));
    setState((prevState) => ({ ...prevState, arrDirtyForm }));

    let checksToDo = [];
    // if we are not setting all future values to zero and the edited value is a cost, we update the EAC total and check that RDA <= EAC
    if (oldValue !== null && type === "amount") {
      checksToDo.push("checkRda");
    }
    // if the edited month is the running month, check that the planned value >= running value
    if (month === state.runningMonthNumber && year === state.runningYearNumber) {
      checksToDo.push("checkRunning");
    }
    if (checksToDo.length > 0) {
      checkPlannedIsNotLessThanCommitted(value, month, type, service, year, projectTaskId, oldValue, checksToDo);
    }
  };

  const checkPlannedIsNotLessThanCommitted = (
    value,
    month,
    type,
    service,
    year,
    projectTaskId,
    oldValue,
    checksToDo = []
  ) => {
    // if checksToDo contains "checkRda": update the EAC total and check that RDA <= EAC
    // if checksToDo contains "checkRunning": check that value >= running value
    let rdaAlertProjectTask = false;
    let plannedRunningErrorProjectTask = false;
    let found = false;
    const customForm = getCustomForm;

    for (const expandedItem of state.wcContentExpanded) {
      if (checksToDo.includes("checkRunning")) {
        for (const workServiceRow of expandedItem.data[0].children) {
          if (workServiceRow.projectTaskId === projectTaskId) {
            if (workServiceRow.activity === service) {
              // if we are here, service is SERVMOI or SERVMOE
              found = true;
              // the changed value should be >= running value
              if (value < (workServiceRow.runningQuantity || 0)) {
                plannedRunningErrorProjectTask = true;
                break;
              }
            } else {
              // get the planned value for the running month (if it has been edited get it from the form, otherwise get it from the original data in workServiceRow)
              let runningMonthPlannedValue = 0;
              const foundIndex = customForm.findIndex(
                (item) =>
                  item.projectTaskId === projectTaskId &&
                  item.year === year &&
                  item.month === month &&
                  item.serviceId === workServiceRow.activity
              );
              if (foundIndex !== -1) {
                runningMonthPlannedValue = customForm[foundIndex].quantity;
              } else {
                workServiceRow.forEach((child) => {
                  if (child.year === state.runningYearNumber) {
                    runningMonthPlannedValue = child.columnValueMap[state.runningMonth];
                    return;
                  }
                });
              }
              // if one service row has a planned value > running value, set error to true. If all are ok, the error remains false until the end of the loop
              plannedRunningErrorProjectTask =
                plannedRunningErrorProjectTask || (workServiceRow.runningQuantity || 0) > runningMonthPlannedValue;
            }
          }
        }
      }

      for (const serviceRow of expandedItem.data[1].children) {
        if (serviceRow.projectTaskId === projectTaskId) {
          if (serviceRow.activity === service) {
            // if service edited is SERVMOI or SERVMOE, we skip the check on its costs row because we already checked the work row (checkRunning) and rda doesn't exist for these services (checkRda is not done)
            if (type === "quantity") {
              continue;
            }
            found = true;
            if (checksToDo.includes("checkRunning")) {
              // the changed value should be >= running value
              if (value < (serviceRow.runningAmount || 0)) {
                plannedRunningErrorProjectTask = true;
                if (!checksToDo.includes("checkRda")) break;
              }
            }
            if (checksToDo.includes("checkRda")) {
              // update TOT_ETC of that service row (somma dei valori pianificati dal mese corrente compreso in poi)
              serviceRow.totalMap.TOT_ETC = (serviceRow.totalMap.TOT_ETC || 0) - oldValue + value;
              // update TOT value (EAC) of that service row
              const oldTotal = serviceRow.totalMap.TOT || 0;
              serviceRow.totalMap.TOT = oldTotal - oldValue + value;
              // update TOT value (EAC) of the costs
              expandedItem.data[1].totalMap.TOT =
                (expandedItem.data[1].totalMap.TOT || 0) - oldTotal + serviceRow.totalMap.TOT;
              // update rdaAlert flag
              if (serviceRow.rda === null) {
                serviceRow.rda = { rda: 0, rdaAlert: false, rdaExtraBudget: false };
              }
              if (serviceRow.rda.rda > serviceRow.totalMap.TOT_ETC) {
                serviceRow.rda.rdaAlert = true;
                rdaAlertProjectTask = true;
                break;
              } else {
                serviceRow.rda.rdaAlert = false;
              }
            }
          } else {
            if (checksToDo.includes("checkRda")) {
              // set rdaAlertProjectTask to true if any service has rdaAlert true
              rdaAlertProjectTask = rdaAlertProjectTask || serviceRow.rda?.rdaAlert;
            }

            if (checksToDo.includes("checkRunning")) {
              // get the planned value for the running month (if it has been edited get it from the form, otherwise get it from the original data in serviceRow)
              let runningMonthPlannedValue = 0;
              const foundIndex = customForm.findIndex(
                (item) =>
                  item.projectTaskId === projectTaskId &&
                  item.year === year &&
                  item.month === month &&
                  item.serviceId === serviceRow.activity
              );
              if (foundIndex !== -1) {
                runningMonthPlannedValue = customForm[foundIndex].amount;
              } else {
                serviceRow.forEach((child) => {
                  if (child.year === state.runningYearNumber) {
                    runningMonthPlannedValue = child.columnValueMap[state.runningMonth];
                    return;
                  }
                });
              }
              // if one service row has a planned value > running value, set error to true. If all are ok, the error remains false until the end of the loop
              plannedRunningErrorProjectTask =
                plannedRunningErrorProjectTask || (serviceRow.runningAmount || 0) > runningMonthPlannedValue;
            }
          }
        }
      }

      if (found) {
        if (checksToDo.includes("checkRda")) {
          // if rdaAlert flag is true, add the project task to rdaAlertProjectTaskIds
          if (rdaAlertProjectTask) {
            props.setRdaAlertProjectTaskIds([
              ...props.rdaAlertProjectTaskIds.filter((id) => id !== projectTaskId),
              projectTaskId,
            ]);
          } else {
            props.setRdaAlertProjectTaskIds(props.rdaAlertProjectTaskIds.filter((id) => id !== projectTaskId));
          }
        }
        if (checksToDo.includes("checkRunning")) {
          // if the planned value is < running value for at least one service, add the project task to plannedRunningErrorProjectTaskIds
          if (plannedRunningErrorProjectTask) {
            props.setPlannedRunningErrorProjectTaskIds([
              ...props.plannedRunningErrorProjectTaskIds.filter((id) => id !== projectTaskId),
              projectTaskId,
            ]);
          } else {
            props.setPlannedRunningErrorProjectTaskIds(
              props.plannedRunningErrorProjectTaskIds.filter((id) => id !== projectTaskId)
            );
          }
        }
        break;
      }
    }
  };

  const handleSave = (id) => async () => {
    let isSaveAllowed = true;
    if (props.rdaAlertProjectTaskIds.includes(id)) {
      showError("", t("projectsManagement.planning.workCostsTab.rdaAlertErrorMessage"));
      isSaveAllowed = false;
    }
    if (props.plannedRunningErrorProjectTaskIds.includes(id)) {
      showError("", t("projectsManagement.planning.workCostsTab.plannedRunningErrorMessage"));
      isSaveAllowed = false;
    }
    // if (!isSaveAllowed) {
    //   return;
    // }

    if (!isEmpty(getCustomForm)) {
      const data = getCustomForm.filter((item) => item.projectTaskId === id);

      if (state.canEditToZeroTasks.includes(id)) {
        let validSave = true;
        data.forEach((item) => {
          if (item.quantity !== 0 || item.amount !== 0) {
            showError("ER036");
            validSave = false;
            return;
          }
        });
        if (!validSave) {
          return;
        }
      }

      const response = await ProjectCostPlan.saveProjectCostPlan(props.projectId, id, data);
      if (!response.error) {
        if (isSaveAllowed) {
          showSuccess();
        } else {
          notification.open({
            duration: 5,
            className: "notification-success",
            message: t("projectsManagement.planning.workCostsTab.saveSuccessTemporarilyPermittedTitle"),
            description: t("projectsManagement.planning.workCostsTab.saveSuccessTemporarilyPermitted"),
          });
        }
        dispatch(setRefreshWorkCosts(true));
      } else {
        showError(get(response, "error.messageCode", "ER000"));
      }
    }
    // const notSavedData = getCustomForm.filter(item => item.projectTaskId !== id)
    // dispatch(setValueWC(notSavedData))
    let arrDirtyForm = state.arrDirtyForm.filter((item) => item !== id);
    setState((prevState) => ({ ...prevState, arrDirtyForm }));
    dispatch(setDirtyWC(false));
  };

  const zeroValues = (projectTaskId, data) => async () => {
    confirm({
      title: t("general.ensureZero"),
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        const openYear = state.wbsData.openYear;
        const openMonthNumber = mapMonths[lowerCase(state.wbsData.openMonth)];
        data.forEach((workOrCostsTable) => {
          workOrCostsTable.children.forEach((serviceRow) => {
            serviceRow.forEach((item) => {
              if (item.year && item.year >= openYear) {
                const partialTotalYearIndex = workOrCostsTable.findIndex((property) => property.year === item.year);
                for (const cvm in item.columnValueMap) {
                  const currentMonthNumber = mapMonths[lowerCase(cvm)];
                  if (item.year > openYear || currentMonthNumber > openMonthNumber) {
                    // change value to zero
                    handleChangeValue(
                      0,
                      currentMonthNumber,
                      serviceRow.isHour ? "quantity" : "amount",
                      serviceRow.activity,
                      item.year,
                      projectTaskId
                    );
                    item.columnValueMap[cvm] = 0;
                    // change partial total value to zero
                    workOrCostsTable[partialTotalYearIndex].columnValueMap[cvm] = 0;
                  }
                }
              }
            });
          });
        });
      },
      onCancel() {},
    });
  };

  const getValueForm = (amount, month, type, service, year, projectTaskId) => {
    let valueForm = amount;
    if (!isEmpty(getCustomForm)) {
      const foundIndex = getCustomForm.findIndex(
        (item) =>
          item.projectTaskId === projectTaskId &&
          item.year === year &&
          item.month === month &&
          item.serviceId === service
      );
      if (foundIndex !== -1) {
        if (type === "quantity") {
          valueForm = getCustomForm[foundIndex].quantity;
        } else {
          valueForm = getCustomForm[foundIndex].amount;
        }
      }
    }
    return valueForm;
  };

  const expandedRowRender = (e) => {
    let data = [];
    state.wcContentExpanded.forEach((item) => {
      if (item.name === e.projectTaskName) {
        data = item.data;
      }
    });
    // Assicuriamoci che expandedTablesRefs.current esista e sia una Map
    if (!expandedTablesRefs.current) {
      expandedTablesRefs.current = new Map();
    }
    return (
      <>
        <div
          ref={(node) => {
            if (node) {
              expandedTablesRefs.current.set(e.key, node);

              // Solo una volta durante il primo montaggio, impostiamo la posizione iniziale
              if (syncEnabled && scrollPosition.x > 0) {
                requestAnimationFrame(() => {
                  const scrollables = node.querySelectorAll(".ant-table-body, .ant-table-content");
                  scrollables.forEach((el) => {
                    if (el) el.scrollLeft = scrollPosition.x;
                  });

                  // Riapplica i listener dopo il render
                  setupScrollListeners();
                });
              }
            } else {
              // Cleanup quando il nodo viene rimosso
              expandedTablesRefs.current.delete(e.key);
            }
          }}
        >
          <Table
            className="expanded-row-wc-table"
            bordered
            dataSource={data}
            columns={state.columnsExpanded}
            size="small"
            pagination={false}
            scroll={{ x: 1500 }}
            // Prestazioni migliorate per tabelle grandi
            virtual={data.length > 50}
            // Evita di ricalcolare la tabella se i dati non sono cambiati
            rowKey={(record) => record.key + record.activity + record.projectTaskId}
          />
        </div>
        <Row type="flex" align="middle" justify="end" className="mr-10 mt-10">
          {state.canEditToZeroTasks.includes(e.projectTaskId) && (
            <Button
              isDisabled={!(userInfo.canEditPlanning && !props.allData.closed && !projectManagement.approved)}
              content={"0€"}
              className="ml-10"
              clickCallback={zeroValues(e.projectTaskId, data)}
            />
          )}
          <Button
            isDisabled={
              !(
                userInfo.canEditPlanning &&
                state.arrDirtyForm.includes(e.projectTaskId) &&
                (projectManagement.enabled || state.canEditToZeroTasks.includes(e.projectTaskId))
              )
            }
            currentIcon={<SaveOutlined className="icon-medium" />}
            className="ml-10"
            clickCallback={handleSave(e.projectTaskId)}
          />
        </Row>
      </>
    );
  };

  const getExpandedRowDataWc = useCallback(
    async (expanded, record) => {
      let expandKey = cloneDeep(state.expandedRowKeys);

      dispatch(setLoading(true));
      try {
        let contentExpanded = [];
        let wcContentExpanded = state.wcContentExpanded;
        let nameProjectTaskId = record.projectTaskName;

        if (expanded) {
          const wbs = await ProjectCostPlan.getProjectCostPlanActivity(props.projectId, record.projectTaskId);
          if (!isEmpty(wbs) && !wbs.error && wbs.data) {
            const dataElaborate = elaborateWCdata(wbs, userInfo);
            contentExpanded = dataElaborate.contentExpanded;
            let obj = { name: nameProjectTaskId, data: contentExpanded };
            wcContentExpanded.push(obj);

            // if initially some planned value < running value, add this project task to error list (we only check the costs because they have all the services)
            let plannedRunningError = false;
            contentExpanded[1].children.forEach((serviceRow) => {
              serviceRow.forEach((child) => {
                if (child.year === state.runningYearNumber) {
                  plannedRunningError =
                    plannedRunningError || (serviceRow.runningAmount || 0) > child.columnValueMap[state.runningMonth];
                  return;
                }
              });
              if (plannedRunningError) return;
            });
            setState((prevState) => ({
              ...prevState,
              wbsData: wbs.data,
            }));
            // Set plannedRunningErrorProjectTaskIds with a function to use the correct current value even inside this callback
            props.setPlannedRunningErrorProjectTaskIds((currentIds) => {
              return plannedRunningError
                ? [...currentIds.filter((id) => id !== record.projectTaskId), record.projectTaskId]
                : currentIds.filter((id) => id !== record.projectTaskId);
            });
            if (state.wcContentExpanded.length === 1) {
              dispatch(initializeForm(record.projectTaskId, dataElaborate.customForm, record.key));
            }
            expandKey.push(record.key);
          } else {
            showError(get(wbs, "error.messageCode", "ER000"));
          }
        } else {
          const index = expandKey.indexOf(record.key);
          if (index !== -1) {
            expandKey.splice(index, 1);
          }
          state.wcContentExpanded.forEach((item, i) => {
            if (item.name === record.projectTaskName) {
              wcContentExpanded.splice(i, 1);
            }
          });
        }

        setState((prevState) => ({
          ...prevState,
          wcContentExpanded: wcContentExpanded,
          expandedRowKeys: expandKey,
        }));

        // Aggiorna gli event listener dopo l'espansione
        requestAnimationFrame(() => {
          setupScrollListeners();
        });
      } finally {
        dispatch(setLoading(false));
      }
    },
    [props.projectId, state.wcContentExpanded, state.expandedRowKeys, setupScrollListeners, state.runningMonth]
  );

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <p>
          {syncEnabled
            ? t("projectsManagement.planning.workCostsTab.syncScrollActive")
            : t("projectsManagement.planning.workCostsTab.disSyncScrollActive")}
        </p>
        <Button
          type={syncEnabled ? "primary" : "default"}
          clickCallback={toggleSync}
          content={
            syncEnabled
              ? t("projectsManagement.planning.workCostsTab.disSyncScroll")
              : t("projectsManagement.planning.workCostsTab.syncScroll")
          }
        />
      </div>
      <div ref={containerRef}>
        <Table
          style={{ position: "relative" }}
          bordered
          dataSource={props.dataContentWP}
          columns={state.columnsDataTable}
          size="small"
          pagination={false}
          scroll={{ x: 1600 }}
          expandable={{
            expandedRowRender,
            expandedRowKeys: state.expandedRowKeys,
            onExpand: (expanded, record) => getExpandedRowDataWc(expanded, record),
          }}
          rowClassName="table-secondary"
        />
      </div>
      <div style={{ marginTop: 16, marginBottom: 16 }}>
        <Button
          type={syncEnabled ? "primary" : "default"}
          clickCallback={toggleSync}
          content={
            syncEnabled
              ? t("projectsManagement.planning.workCostsTab.disSyncScroll")
              : t("projectsManagement.planning.workCostsTab.syncScroll")
          }
        />
      </div>
      <RdaDetailModal modalState={rdaDetailModalState} setModalState={setRdaDetailModalState} />
    </>
  );
};

export default WCTable;
